import { useEffect, useState, Fragment } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, API, eur } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart3, Layers, FileText, FileSpreadsheet, Package, Landmark, BookText } from "lucide-react";

const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

export default function FactuurboekOverzicht() {
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname.startsWith("/rapporten") ? "/rapporten" : "/factuurboek";
  const [from, setFrom] = useState(firstOfYear());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState("");
  const [breakdown, setBreakdown] = useState({});

  const SPECIAL = { autokosten: `${base}/autokosten`, nutsvoorzieningen: `${base}/energie` };
  const onCatClick = async (key) => {
    if (SPECIAL[key]) { navigate(SPECIAL[key]); return; }
    if (expanded === key) { setExpanded(""); return; }
    setExpanded(key);
    if (!breakdown[key]) {
      try {
        const r = await api.get("/factuurboek/categorie/breakdown", { params: { key, from_date: from, to_date: to } });
        setBreakdown((b) => ({ ...b, [key]: r.data.suppliers }));
      } catch { setBreakdown((b) => ({ ...b, [key]: [] })); }
    }
  };

  const load = () => {
    setLoading(true);
    api.get("/factuurboek/summary", { params: { from_date: from, to_date: to } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async (report, fmt) => {
    const key = `${report}-${fmt}`;
    setBusy(key);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/factuurboek/export/${report}?from_date=${from}&to_date=${to}&format=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("mislukt");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const name = report === "handelsgoederen" ? "Handelsgoederen" : report === "bankkosten" ? "Bankkosten" : "BTW-boek_kosten";
      a.download = `${name}_${from}_${to}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${name} geëxporteerd (${fmt.toUpperCase()})`);
    } catch (e) { toast.error("Kon rapport niet exporteren"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="factuur-overzicht-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
          <BarChart3 className="h-7 w-7 text-indigo-600" /> Overzicht aankopen
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Aankopen per kostencategorie met <b>excl. btw</b> (de eigenlijke kost), <b>btw</b> (terug te vorderen) en <b>incl. btw</b>.</p>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="space-y-1.5"><Label>Van</Label><Input type="date" data-testid="ov-from" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Tot</Label><Input type="date" data-testid="ov-to" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="ov-refresh" onClick={load}>Tonen</Button>
        </CardContent>
      </Card>

      <Card className="shadow-sm border-indigo-100">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">BTW-boek — volledig kosten-overzicht</CardTitle>
          <p className="text-sm text-muted-foreground">Alle kosten per soort → per leverancier → per factuur (leverancier, datum, factuurnr., excl. btw, btw, incl. btw). Enkel bevestigde facturen.</p>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border p-4 space-y-3 bg-indigo-50/40" data-testid="export-kostenboek">
            <div className="flex items-center gap-2 font-medium"><BookText className="h-5 w-5 text-indigo-600" /> BTW-boek (alle kosten, per categorie)</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={busy === "kostenboek-pdf"} data-testid="export-kb-pdf" onClick={() => download("kostenboek", "pdf")}>
                <FileText className="h-4 w-4 mr-1.5" /> {busy === "kostenboek-pdf" ? "Bezig…" : "PDF"}
              </Button>
              <Button size="sm" variant="outline" disabled={busy === "kostenboek-xlsx"} data-testid="export-kb-xlsx" onClick={() => download("kostenboek", "xlsx")}>
                <FileSpreadsheet className="h-4 w-4 mr-1.5" /> {busy === "kostenboek-xlsx" ? "Bezig…" : "Excel"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm border-indigo-100">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Exportrapporten (FactuurKompas-stijl)</CardTitle>
          <p className="text-sm text-muted-foreground">Voor de gekozen periode. Enkel bevestigde facturen worden meegenomen.</p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border p-4 space-y-3" data-testid="export-handelsgoederen">
            <div className="flex items-center gap-2 font-medium"><Package className="h-5 w-5 text-indigo-600" /> Aankoop handelsgoederen</div>
            <p className="text-xs text-muted-foreground">Gegroepeerd per leverancier, met btw-splits per tarief (0/6/21).</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={busy === "handelsgoederen-pdf"} data-testid="export-hg-pdf" onClick={() => download("handelsgoederen", "pdf")}>
                <FileText className="h-4 w-4 mr-1.5" /> {busy === "handelsgoederen-pdf" ? "Bezig…" : "PDF"}
              </Button>
              <Button size="sm" variant="outline" disabled={busy === "handelsgoederen-xlsx"} data-testid="export-hg-xlsx" onClick={() => download("handelsgoederen", "xlsx")}>
                <FileSpreadsheet className="h-4 w-4 mr-1.5" /> {busy === "handelsgoederen-xlsx" ? "Bezig…" : "Excel"}
              </Button>
            </div>
          </div>
          <div className="rounded-lg border p-4 space-y-3" data-testid="export-bankkosten">
            <div className="flex items-center gap-2 font-medium"><Landmark className="h-5 w-5 text-indigo-600" /> Bankkosten</div>
            <p className="text-xs text-muted-foreground">Gesplitst in terminal-, transactie-, rente- en diverse financiële kosten.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={busy === "bankkosten-pdf"} data-testid="export-bk-pdf" onClick={() => download("bankkosten", "pdf")}>
                <FileText className="h-4 w-4 mr-1.5" /> {busy === "bankkosten-pdf" ? "Bezig…" : "PDF"}
              </Button>
              <Button size="sm" variant="outline" disabled={busy === "bankkosten-xlsx"} data-testid="export-bk-xlsx" onClick={() => download("bankkosten", "xlsx")}>
                <FileSpreadsheet className="h-4 w-4 mr-1.5" /> {busy === "bankkosten-xlsx" ? "Bezig…" : "Excel"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? <div className="py-16 text-center text-muted-foreground">Laden…</div> : data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="shadow-sm">
              <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Layers className="h-5 w-5 text-indigo-600" /> Per kostencategorie</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Categorie</TableHead><TableHead className="text-right">Aantal</TableHead><TableHead className="text-right">Excl. btw</TableHead><TableHead className="text-right">BTW</TableHead><TableHead className="text-right">Incl. btw</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.by_category.map((c) => {
                      const special = !!SPECIAL[c.key];
                      const isOpen = expanded === c.key;
                      return (
                      <Fragment key={c.key}>
                      <TableRow data-testid={`ov-cat-${c.key}`}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => onCatClick(c.key)}>
                        <TableCell>{c.label}
                          {special ? <span className="ml-2 text-xs text-amber-700">▸ per {c.key === "autokosten" ? "auto" : "vestiging"}</span>
                            : <span className="ml-2 text-xs text-muted-foreground">{isOpen ? "▾" : "▸"} per leverancier</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{c.count}</TableCell>
                        <TableCell className="text-right tabular-nums">{eur(c.base)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{eur(c.vat)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium">{eur(c.total)}</TableCell>
                      </TableRow>
                      {isOpen && !special && (breakdown[c.key] || []).map((s) => (
                        <TableRow key={`${c.key}-${s.supplier}`} className="bg-muted/20" data-testid={`ov-sub-${c.key}-${s.supplier}`}>
                          <TableCell className="pl-8 text-sm text-muted-foreground">{s.supplier} <span className="text-xs">({s.count})</span></TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right tabular-nums text-sm">{eur(s.base)}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{eur(s.vat)}</TableCell>
                          <TableCell className="text-right tabular-nums text-sm">{eur(s.total)}</TableCell>
                        </TableRow>
                      ))}
                      {isOpen && !special && breakdown[c.key] && breakdown[c.key].length === 0 && (
                        <TableRow className="bg-muted/20"><TableCell colSpan={5} className="pl-8 text-sm text-muted-foreground">Geen detail beschikbaar.</TableCell></TableRow>
                      )}
                      </Fragment>
                      );
                    })}
                    {data.by_category.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Geen bevestigde facturen in deze periode.</TableCell></TableRow>}
                    <TableRow className="bg-muted/40 font-semibold"><TableCell>Totaal</TableCell><TableCell className="text-right">{data.count}</TableCell><TableCell className="text-right tabular-nums" data-testid="ov-total-base">{eur(data.base)}</TableCell><TableCell className="text-right tabular-nums" data-testid="ov-total-vat">{eur(data.vat)}</TableCell><TableCell className="text-right tabular-nums" data-testid="ov-total">{eur(data.total)}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card className="shadow-sm">
              <CardHeader><CardTitle className="font-heading text-lg">Handelsgoederen per btw-tarief</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Tarief</TableHead><TableHead className="text-right">Maatstaf</TableHead><TableHead className="text-right">BTW</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {data.goods_by_rate.map((g) => (
                      <TableRow key={g.rate} data-testid={`ov-rate-${g.rate}`}>
                        <TableCell>{g.rate}%</TableCell>
                        <TableCell className="text-right tabular-nums">{eur(g.base)}</TableCell>
                        <TableCell className="text-right tabular-nums">{eur(g.vat)}</TableCell>
                      </TableRow>
                    ))}
                    {data.goods_by_rate.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">Geen handelsgoederen in deze periode.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
